import React, { useEffect, useState } from 'react';
import Icon from '../components/AppIcon';
import SiteHeader from '../components/SiteHeader';
import {
  FALLBACK_DOWNLOADS,
  detectPlatform,
  fetchDesktopRelease,
  formatReleaseDate,
  formatSize,
} from '../utils/desktopRelease';

const PLATFORMS = {
  windows: {
    label: 'Windows',
    icon: 'Monitor',
    requirement: 'Windows 10 veya 11, 64-bit',
  },
  mac: {
    label: 'macOS',
    icon: 'Laptop',
    requirement: 'macOS 12 veya üzeri (Apple Silicon ve Intel)',
  },
};

const FEATURES = [
  {
    icon: 'Printer',
    title: 'Yerel yazıcı',
    text: 'Adisyon ve mutfak fişi ağdaki termal yazıcıya doğrudan gider; tarayıcı penceresi açılmaz.',
  },
  {
    icon: 'Receipt',
    title: 'Yazarkasa (ÖKC) — hazırlanıyor',
    // Entegrasyon yazıldı ama fiziksel cihazda henüz doğrulanmadı (TODO B3.1);
    // "aktarılır" demek satılmış ama çalışmayan özellik vaadi olurdu.
    text: 'Satışın mali fiş olarak yazarkasaya aktarılması cihaz doğrulamasında. Windows kasalarda gelecek.',
  },
  {
    icon: 'WifiOff',
    title: 'İnternet kesilse de',
    text: 'Masa açma, sipariş ve nakit tahsilat bağlantı yokken de alınır; bağlantı gelince gönderilir.',
  },
  {
    icon: 'RefreshCw',
    title: 'Kendiliğinden güncellenir',
    text: 'Yeni sürüm arka planda iner ve kasa kapanırken kurulur — servis sırasında kesinti olmaz.',
  },
];

function DownloadButton({ platform, info, primary, version }) {
  const meta = PLATFORMS[platform];
  const href = info?.stableUrl || FALLBACK_DOWNLOADS[platform].stableUrl;
  const size = formatSize(info?.size);
  return (
    <a
      href={href}
      className={
        primary
          ? 'inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-primary text-white shadow-lg hover:opacity-95 transition'
          : 'inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-gray-300 text-foreground hover:border-primary transition'
      }
      data-platform={platform}
    >
      <Icon name={primary ? 'Download' : meta.icon} size={primary ? 22 : 18} />
      <span className="text-left">
        <span className="block font-poppins font-heading-semibold">
          {meta.label} için indir
        </span>
        {(version || size) && (
          <span className={`block text-xs ${primary ? 'text-white/80' : 'text-text-secondary'}`}>
            {[version && `Sürüm ${version}`, size].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
    </a>
  );
}

const DownloadPage = () => {
  const [release, setRelease] = useState(null);
  const [platform] = useState(() => detectPlatform());

  useEffect(() => {
    let alive = true;
    fetchDesktopRelease().then((manifest) => alive && setRelease(manifest));
    return () => {
      alive = false;
    };
  }, []);

  const downloads = release?.downloads || {};
  const primary = platform === 'mac' ? 'mac' : 'windows';
  const secondary = primary === 'mac' ? 'windows' : 'mac';
  const releasedAt = formatReleaseDate(release?.releasedAt);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <section className="text-center">
          <p className="font-open-sans text-sm uppercase tracking-wide text-secondary mb-3">
            POS Sistemi modülü ile
          </p>
          <h1 className="font-poppins font-heading-bold text-3xl md:text-5xl text-foreground mb-4">
            Kasa Uygulaması
          </h1>
          <p className="font-open-sans text-lg text-text-secondary max-w-2xl mx-auto mb-8">
            Kasa terminaliniz için Cafe Otomasyonu. Tarayıcıdaki POS ile aynı ekranlar; üstüne yazıcı,
            yazarkasa ve internet kesildiğinde de çalışma.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <DownloadButton platform={primary} info={downloads[primary]} primary version={release?.version} />
            <DownloadButton platform={secondary} info={downloads[secondary]} version={release?.version} />
          </div>

          {platform === 'mobile' && (
            <p className="mt-4 font-open-sans text-sm text-text-secondary">
              Bu uygulama bilgisayar içindir. Telefonda POS'u tarayıcıdan kullanabilirsiniz.
            </p>
          )}
          {releasedAt && (
            <p className="mt-4 font-open-sans text-xs text-text-secondary">Yayın tarihi: {releasedAt}</p>
          )}
        </section>

        <section className="mt-14 grid gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-5 rounded-xl border border-gray-200">
              <Icon name={f.icon} size={22} className="text-primary mb-3" />
              <h2 className="font-poppins font-heading-semibold text-lg text-foreground mb-1">{f.title}</h2>
              <p className="font-open-sans text-sm text-text-secondary leading-relaxed">{f.text}</p>
            </div>
          ))}
        </section>

        <section className="mt-14">
          <h2 className="font-poppins font-heading-bold text-2xl text-foreground mb-4">Kurulum</h2>
          <ol className="list-decimal pl-6 space-y-3 font-open-sans text-text-secondary leading-relaxed">
            <li>Kurulum dosyasını indirip çalıştırın. Yönetici izni gerekmez.</li>
            <li>
              Windows <strong>“Windows kişisel bilgisayarınızı korudu”</strong> uyarısı gösterebilir:{' '}
              <strong>Ek bilgi</strong> → <strong>Yine de çalıştır</strong>. Uygulamamız henüz Microsoft’un
              tanıdığı bir yayıncı sertifikası taşımıyor; uyarı yalnızca ilk kurulumda çıkar.
            </li>
            <li>
              macOS’ta ilk açılışta uygulamaya sağ tıklayıp <strong>Aç</strong>’ı seçin.
            </li>
            <li>Cafe Otomasyonu hesabınızla giriş yapın; personel PIN ile kasaya geçer.</li>
          </ol>
        </section>

        <section className="mt-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-poppins font-heading-bold text-2xl text-foreground mb-4">Sistem gereksinimleri</h2>
            <ul className="space-y-2 font-open-sans text-text-secondary">
              {Object.entries(PLATFORMS).map(([key, p]) => (
                <li key={key}>
                  <strong className="text-foreground">{p.label}:</strong> {p.requirement}
                </li>
              ))}
              <li>
                <strong className="text-foreground">Yazarkasa:</strong> entegrasyon hazırlanıyor; yalnızca Windows kasalarda olacak.
              </li>
            </ul>
          </div>
          <div>
            <h2 className="font-poppins font-heading-bold text-2xl text-foreground mb-4">Güncellemeler</h2>
            <p className="font-open-sans text-text-secondary leading-relaxed">
              Kurulumdan sonra bir daha indirmeniz gerekmez. Yeni sürüm arka planda iner, kasa kapanırken
              kurulur. İnternet kesikken alınıp henüz gönderilmemiş işlem varsa kurulum, işlemler
              gönderilene kadar bekler.
            </p>
            {release?.notes && (
              <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                <p className="font-poppins font-heading-semibold text-sm text-foreground mb-1">
                  {release.version} sürümünde
                </p>
                <p className="font-open-sans text-sm text-text-secondary whitespace-pre-line">{release.notes}</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default DownloadPage;
